import { useMemo } from 'react';

import { useSeenFilter } from 'Components/VehicleList/SeenFilter';
import { useSeenFilterExperimentation } from 'Services/optimizely/experimentations/useSeenFilterExperimentation';
import type { Pagination } from 'Types/pagination';
import type { VehicleListItem } from 'Types/vehicle';
import useSaleTimes from 'Utilities/hooks/useSaleTimes';
import useUser from 'Utilities/hooks/useUser';
import { filterVehiclesBySeen, VIEWING_FILTER } from 'Utilities/seenVehicles/seenVehicles';

interface UseSeenFilterAutoLoadParams {
	pagination: Pagination;
	vehicleCount: number;
	vehicles: VehicleListItem[];
}

interface UseSeenFilterAutoLoadResult {
	filteredVehicles: VehicleListItem[];
	vehiclesCount: number;
}

export const useSeenFilterAutoLoad = ({
	pagination,
	vehicleCount,
	vehicles,
}: UseSeenFilterAutoLoadParams): UseSeenFilterAutoLoadResult => {
	const [seenFilter] = useSeenFilter();
	const isSeenFilterEnabled = useSeenFilterExperimentation();
	const user = useUser();
	const { todaysSaleStartedAt } = useSaleTimes();

	const filteredVehicles = useMemo(
		() => (isSeenFilterEnabled ? filterVehiclesBySeen(vehicles, user?.id, seenFilter, todaysSaleStartedAt) : vehicles),
		[isSeenFilterEnabled, vehicles, user?.id, seenFilter, todaysSaleStartedAt],
	);

	const vehiclesCount = useMemo(() => {
		if (seenFilter === VIEWING_FILTER.ALL) {
			return vehicleCount;
		}
		if (pagination.hasMore) {
			return -1;
		}
		if (filteredVehicles.length === vehicles.length) {
			return vehicleCount;
		}

		return vehicleCount - (vehicles.length - filteredVehicles.length);
	}, [filteredVehicles.length, pagination.hasMore, seenFilter, vehicleCount, vehicles.length]);

	return { filteredVehicles, vehiclesCount };
};
